/**
 * NC-1579: client-side normalisation of RDP source-IP entries.
 *
 * Mirrors the API guard in `web-portal/api/shared/rdpAccess.ts` (and the PS
 * authority `Test-RdpSourceCidr`) so a typo is caught in the dialog instead of
 * as a 400 after "Speichern". The server stays authoritative — this only
 * improves the message and adds one convenience: a bare address (what a tutor
 * usually has) becomes a single-host `/32`.
 */

/** Company egress — the default whitelist when nothing is entered. */
export const DEFAULT_RDP_SOURCE_CIDR = '185.196.29.66/32';

/** Narrowest acceptable prefix — mirrors MIN_RDP_SOURCE_PREFIX (NC-1212). */
export const MIN_RDP_SOURCE_PREFIX = 24;

/** Mirrors MAX_RDP_SOURCE_CIDRS in rdpToggleService.ts. */
export const MAX_RDP_SOURCE_CIDRS = 10;

const OCTET = '(25[0-5]|2[0-4][0-9]|1?[0-9]?[0-9])';
const IPV4 = new RegExp(`^(${OCTET}\\.){3}${OCTET}$`);
const IPV4_CIDR = new RegExp(`^(${OCTET}\\.){3}${OCTET}/(3[0-2]|[12]?[0-9])$`);
const OPEN_WILDCARDS = ['0.0.0.0/0', '0.0.0.0', 'internet', '*', '::/0'];

export type CidrCheck = { ok: true; cidr: string } | { ok: false; error: string };

/** Validate one entry; a bare IPv4 address is completed to `/32`. */
export function normalizeRdpSourceCidr(raw: string): CidrCheck {
  const value = raw.trim();
  if (!value) return { ok: false, error: 'Bitte eine IP-Adresse eingeben.' };
  if (OPEN_WILDCARDS.includes(value.toLowerCase())) {
    return { ok: false, error: `${value} würde RDP fürs ganze Internet öffnen — nicht erlaubt.` };
  }
  const cidr = IPV4.test(value) ? `${value}/32` : value;
  if (!IPV4_CIDR.test(cidr)) {
    return { ok: false, error: `${value} ist keine gültige IPv4-Adresse (z. B. 203.0.113.42 oder 203.0.113.0/24).` };
  }
  const prefix = parseInt(cidr.split('/')[1], 10);
  if (prefix < MIN_RDP_SOURCE_PREFIX) {
    return { ok: false, error: `${cidr} ist zu breit — höchstens /${MIN_RDP_SOURCE_PREFIX} (256 Adressen) erlaubt.` };
  }
  return { ok: true, cidr };
}

/**
 * Split a free-text list (comma, semicolon, whitespace or newline separated),
 * normalise every entry and de-duplicate. Returns the valid CIDRs plus the
 * first error, so a form can show one precise message.
 */
export function parseRdpSourceCidrList(raw: string): { cidrs: string[]; error?: string } {
  const parts = raw.split(/[\s,;]+/).map((p) => p.trim()).filter(Boolean);
  const cidrs: string[] = [];
  for (const part of parts) {
    const check = normalizeRdpSourceCidr(part);
    if (!check.ok) return { cidrs, error: check.error };
    if (!cidrs.includes(check.cidr)) cidrs.push(check.cidr);
  }
  if (cidrs.length > MAX_RDP_SOURCE_CIDRS) {
    return { cidrs, error: `Höchstens ${MAX_RDP_SOURCE_CIDRS} IP-Adressen pro Schulung.` };
  }
  return { cidrs };
}
